<template>
  <div class="my-orders-container">
    <el-container>
      <el-main>
        <div class="header">
          <h1>我的打印订单</h1>
        </div>
        <div class="table-container">
          <!-- 添加批量支付按钮 -->
          <div class="batch-actions" v-if="selectedOrders.length > 0">
            <div class="batch-payment-info">
              <div class="coupon-selection">
                <el-select
                  v-model="selectedCoupon"
                  placeholder="选择优惠券"
                  clearable
                  @change="recalculatePrice"
                  style="width: 200px"
                >
                  <el-option
                    v-for="coupon in availableCoupons"
                    :key="coupon.code"
                    :label="getCouponLabel(coupon)"
                    :value="coupon"
                  >
                    <div class="coupon-option">
                      <span class="coupon-type">
                        {{ coupon.type === "fixed" ? "满减券" : "折扣券" }}
                      </span>
                      <span class="coupon-value">
                        {{
                          coupon.type === "fixed"
                            ? `¥${coupon.value}`
                            : `${(coupon.value / 10).toFixed(1)}折`
                        }}
                      </span>
                      <span
                        class="coupon-condition"
                        v-if="coupon.min_amount > 0"
                      >
                        满{{ coupon.min_amount }}可用
                      </span>
                    </div>
                  </el-option>
                </el-select>
              </div>
              <div class="price-info">
                <template v-if="discountAmount > 0">
                  <span class="original-price">原价：¥{{ originalPrice }}</span>
                  <span class="discount-amount"
                    >优惠：-¥{{ discountAmount }}</span
                  >
                </template>
                <span class="final-price">总价：¥{{ totalPrice }}</span>
              </div>
              <el-button type="primary" @click="batchPayment"
                >批量支付 ({{ selectedOrders.length }}个订单)</el-button
              >
            </div>
          </div>
          <!-- 订单列表 -->
          <el-table
            :data="paginatedOrders"
            style="width: 100%"
            border
            @selection-change="handleSelectionChange"
          >
            <el-table-column
              type="selection"
              width="55"
              :selectable="(row) => row.status === 'unpaid'"
            >
            </el-table-column>
            <el-table-column
              prop="orderId"
              label="订单编号"
              min-width="160"
            ></el-table-column>
            <el-table-column
              prop="createTime"
              label="创建时间"
              min-width="160"
            ></el-table-column>
            <el-table-column prop="status" label="订单状态" min-width="100">
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="printType" label="打印类型" min-width="100">
              <template #default="scope">
                {{ scope.row.printType === "black" ? "黑白" : "彩色" }}
              </template>
            </el-table-column>
            <el-table-column prop="pay" label="实付金额" min-width="100">
              <template #default="scope">
                ¥{{ scope.row.pay.toFixed(2) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180">
              <template #default="scope">
                <div class="operation-buttons">
                  <el-button size="small" @click="viewOrderDetail(scope.row)"
                    >查看详情</el-button
                  >
                  <el-button
                    v-if="scope.row.status === 'unpaid'"
                    size="small"
                    type="primary"
                    @click="showPaymentQRCode(scope.row)"
                    >去支付</el-button
                  >
                  <el-button
                    v-if="scope.row.status === 'waiting'"
                    size="small"
                    type="danger"
                    @click="cancelOrder(scope.row)"
                    >取消订单</el-button
                  >
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalOrders"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <!-- 订单详情弹窗 -->
        <el-dialog
          title="订单详情"
          v-model="detailDialogVisible"
          width="70%"
          top="5vh"
        >
          <div v-if="currentOrder" class="order-detail">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="订单编号">{{
                currentOrder.orderId
              }}</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{
                currentOrder.createTime
              }}</el-descriptions-item>
              <el-descriptions-item label="订单状态">
                <el-tag :type="getStatusType(currentOrder.status)">
                  {{ getStatusText(currentOrder.status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="打印类型">
                {{ currentOrder.printType === "black" ? "黑白" : "彩色" }}
              </el-descriptions-item>
              <el-descriptions-item
                label="翻页方式"
                v-if="currentOrder.printSide === '双面'"
              >
                {{ currentOrder.method }}
              </el-descriptions-item>
              <el-descriptions-item label="打印面数">
                {{ currentOrder.page }}
              </el-descriptions-item>
              <el-descriptions-item label="装订方式">
                {{ currentOrder.binding }}
              </el-descriptions-item>
              <el-descriptions-item label="纸张大小">
                {{ currentOrder.paperSize }}
              </el-descriptions-item>
              <el-descriptions-item label="纸张材质">
                {{ currentOrder.paperMaterial }}
              </el-descriptions-item>
              <el-descriptions-item label="纸张厚度">
                {{ currentOrder.thick }}
              </el-descriptions-item>
              <el-descriptions-item label="封面内容">
                {{
                  currentOrder.fm_content
                    ? currentOrder.fm_content === "空白"
                      ? "空白封面"
                      : currentOrder.fm_content === "首页"
                      ? "文件首页为封面"
                      : "文件首尾为封面封底"
                    : "不添加封面"
                }}
              </el-descriptions-item>
              <el-descriptions-item
                label="封面类型"
                v-if="currentOrder.fm_content"
              >
                {{ currentOrder.fm_type }}
              </el-descriptions-item>
              <!-- <el-descriptions-item
                label="封面工艺"
                v-if="currentOrder.fm_content"
              >
                {{ currentOrder.fm_method }}
              </el-descriptions-item> -->
              <el-descriptions-item label="配送地址">
                {{ currentOrder.deliveryAddress }}
              </el-descriptions-item>
              <el-descriptions-item label="备注">
                {{ currentOrder.note }}
              </el-descriptions-item>
              <!-- <el-descriptions-item label="配送时间">{{
                currentOrder.deliveryTime
              }}</el-descriptions-item> -->
              <el-descriptions-item label="总价"
                >¥{{ currentOrder.totalPrice.toFixed(2) }}</el-descriptions-item
              >
              <!-- <el-descriptions-item label="优惠券使用">
                <span v-if="currentOrder.coupon_info">
                  已使用优惠券 ({{ currentOrder.coupon_info.code }})
                  <template v-if="currentOrder.coupon_info.type === 'fixed'">
                    优惠金额：¥{{ currentOrder.coupon_info.value.toFixed(2) }}
                  </template>
                  <template v-else>
                    优惠：{{
                      (currentOrder.coupon_info.value / 10).toFixed(1)
                    }}折
                  </template>
                </span>
                <span v-else>未使用优惠券</span>
              </el-descriptions-item> -->
              <el-descriptions-item label="实付金额">
                {{ currentOrder.pay.toFixed(2) }}
                <!-- <span class="actual-price">
                  <template v-if="currentOrder.coupon_info">
                    <template v-if="currentOrder.coupon_info.type === 'fixed'">
                      ¥{{
                        Math.max(
                          0,
                          currentOrder.totalPrice -
                            currentOrder.coupon_info.value
                        ).toFixed(2)
                      }}
                    </template>
                    <template v-else>
                      ¥{{
                        Math.max(
                          0,
                          currentOrder.totalPrice *
                            (currentOrder.coupon_info.value / 1000)
                        ).toFixed(2)
                      }}
                    </template>
                  </template>
                  <template v-else>
                    ¥{{ currentOrder.totalPrice.toFixed(2) }}
                  </template>
                </span> -->
              </el-descriptions-item>
            </el-descriptions>

            <!-- 文件列表 -->
            <div class="file-list">
              <h3>文件操作</h3>
              <el-button
                size="small"
                type="primary"
                @click="downloadFile(currentOrder.orderId)"
              >
                下载打印文件
              </el-button>
            </div>
          </div>
        </el-dialog>

        <!-- 支付二维码弹窗 -->
        <el-dialog
          title="订单支付"
          v-model="paymentDialogVisible"
          width="400px"
          center
          :close-on-click-modal="false"
          :close-on-press-escape="false"
          :show-close="false"
        >
          <div class="payment-dialog-content">
            <div class="order-info">
              <template v-if="selectedOrders.length > 1">
                <p class="order-number">
                  批量支付 {{ selectedOrders.length }} 个订单
                </p>
                <div class="order-list">
                  <div
                    v-for="order in selectedOrders"
                    :key="order.orderId"
                    class="order-list-item"
                  >
                    <span class="order-id">订单号：{{ order.orderId }}</span>
                    <span class="order-price">¥{{ order.pay.toFixed(2) }}</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <p class="order-number">订单号：{{ order_id }}</p>
              </template>
              <p class="order-amount">总支付金额：¥{{ totalPrice }}</p>
            </div>
            <div class="qr-code-container">
              <img
                :src="qrCodeUrl"
                alt="支付二维码"
                class="qr-code-image"
                v-if="qrCodeUrl"
              />
              <div class="qr-code-loading" v-else>
                <el-icon class="is-loading"><loading /></el-icon>
                <span>正在生成支付二维码...</span>
              </div>
            </div>
            <div class="payment-tips">
              <p>请使用微信扫描二维码完成支付</p>
              <p class="expire-time" v-if="expireTime">
                支付倒计时：{{ expireTime }}
              </p>
            </div>
          </div>
          <template #footer>
            <span class="dialog-footer">
              <el-button @click="cancelPayment">取消支付</el-button>
            </span>
          </template>
        </el-dialog>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import axios from "axios";

const API_BASE_URL = "https://www.xiaoliangtongxue.cn/api"; // 后端API基础URL

const orderApi = {
  // 获取订单列表
  async getOrders(userId = null) {
    const url = userId
      ? `${API_BASE_URL}/order?user_id=${userId}`
      : `${API_BASE_URL}/order`;
    return await axios.get(url);
  },

  // 取消订单
  async cancelOrder(orderId) {
    return await axios.post(`${API_BASE_URL}/order/${orderId}/cancel`);
  },

  // 下载文件
  async downloadFile(fileId) {
    return await axios.get(`${API_BASE_URL}/files/${fileId}`, {
      responseType: "blob",
    });
  },
};

export default {
  name: "MyOrders",
  data() {
    return {
      totalPrice: 0,
      currentPage: 1,
      pageSize: 10,
      orderList: [],
      detailDialogVisible: false,
      currentOrder: null,
      // 添加支付相关的数据
      paymentDialogVisible: false,
      qrCodeUrl: "",
      expireTime: "",
      paymentTimer: null,
      paymentCheckInterval: null,
      order_id: "",
      selectedOrders: [],
      selectedCoupon: null,
      availableCoupons: [],
      originalPrice: 0,
      discountAmount: 0,
      isPaying: false,
    };
  },
  created() {
    this.$notify.info({
      title: "退款须知",
      message: "退款请联系平台客服，电话 19370962532（工作日：9：00~18：00）",
      duration: 30000,
    });
    this.fetchOrders();
    this.fetchCoupons(); // 在组件创建时获取优惠券
  },
  computed: {
    filteredOrders() {
      let result = [...this.orderList];
      // 前端按ID降序排序
      result.sort((a, b) => b.orderId - a.orderId);
      return result;
    },
    paginatedOrders() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredOrders.slice(startIndex, endIndex);
    },
    totalOrders() {
      return this.filteredOrders.length;
    },
  },
  methods: {
    handleSizeChange(val) {
      this.pageSize = val;
      // 当每页条数改变时，重置到第一页
      this.currentPage = 1;
    },
    handleCurrentChange(val) {
      this.currentPage = val;
    },
    // 获取订单列表
    async fetchOrders() {
      try {
        // 直接从localStorage获取用户ID
        const userId = localStorage.getItem("userId");

        if (!userId) {
          this.$message.error("请先登录");
          this.$router.push("/login");
          return;
        }

        // 如果是管理员，获取所有订单
        if (userId === "admin") {
          const response = await orderApi.getOrders();
          if (response.data) {
            this.orderList = response.data.map((order) => ({
              orderId: order.id,
              createTime: order.create_time,
              status: order.state,
              printType: order.color ? "color" : "black",
              printSide: order.page,
              paperSize: order.size,
              paperMaterial: order.material,
              deliveryAddress: order.address,
              totalPrice: order.price,
              coupon_info: order.coupon_info,
              files: [], // 文件列表需要后端提供
              page: order.page,
              binding: order.binding,
              method: order.method,
              fm_content: order.fm_content,
              fm_type: order.fm_type,
              fm_method: order.fm_method,
              thick: order.thick,
              suoyin: order.suoyin,
              pay: order.pay,
              note: order.note,
            }));
          }
        } else {
          console.log("普通");
          // 普通用户只获取自己的订单
          const response = await orderApi.getOrders(parseInt(userId));
          console.log(response);
          if (response.data) {
            this.orderList = response.data.map((order) => ({
              orderId: order.id,
              createTime: order.create_time,
              status: order.state,
              printType: order.color ? "color" : "black",
              printSide: order.page,
              paperSize: order.size,
              paperMaterial: order.material,
              deliveryAddress: order.address,
              totalPrice: order.price,
              coupon_info: order.coupon_info,
              files: [], // 文件列表需要后端提供
              page: order.page,
              binding: order.binding,
              method: order.method,
              fm_content: order.fm_content,
              fm_type: order.fm_type,
              fm_method: order.fm_method,
              thick: order.thick,
              suoyin: order.suoyin,
              pay: order.pay,
              note: order.note,
            }));
          }
          console.log(this.orderList);
        }
      } catch (error) {
        this.$message.error("获取订单列表失败");
        console.error("获取订单列表失败:", error);
      }
    },
    getStatusType(status) {
      const statusMap = {
        unpaid: "warning",
        printing: "primary",
        delivering: "info",
        completed: "success",
        cancelled: "danger",
      };
      return statusMap[status] || "info";
    },
    getStatusText(status) {
      const statusMap = {
        unpaid: "待支付",
        printing: "打印中",
        delivering: "配送中",
        completed: "已完成",
        cancelled: "已取消",
      };
      return statusMap[status] || "未知状态";
    },
    viewOrderDetail(order) {
      this.currentOrder = order;
      this.detailDialogVisible = true;
    },
    async cancelOrder(order) {
      try {
        await this.$confirm("确定要取消该订单吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        });

        await orderApi.cancelOrder(order.orderId);
        this.$message.success("订单已取消");
        await this.fetchOrders(); // 刷新订单列表
      } catch (error) {
        if (error !== "cancel") {
          // 用户点击取消按钮时不显示错误
          this.$message.error("取消订单失败");
          console.error("取消订单失败:", error);
        }
      }
    },
    async downloadFile(orderId) {
      try {
        // 调用后端的文件下载接口
        const response = await fetch(
          `https://www.xiaoliangtongxue.cn/api/download/${orderId}`,
          {
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("文件下载失败");
        }

        // 获取文件blob
        const blob = await response.blob();

        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `打印文件_订单${orderId}.pdf`; // 可以自定义文件名
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        this.$message.success("文件下载成功");
      } catch (error) {
        console.error("下载文件失败:", error);
        this.$message.error("下载文件失败，请稍后重试");
      }
    },

    async showPaymentQRCode(order) {
      if (this.isPaying) {
        this.$message.warning("正在处理支付请求，请勿重复点击");
        return;
      }

      this.isPaying = true;

      try {
        this.currentOrder = order;
        const orderId = `PRINT_${String(order.orderId).padStart(6, "0")}`;
        this.order_id = orderId;
        const order_amount = (Number(order.pay) || 0).toFixed(2);
        this.totalPrice = order_amount;

        const payResponse = await fetch(
          `https://www.xiaoliangtongxue.cn/api/pay`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              order_no: orderId,
              amount: order_amount,
              orders: [order.orderId],
            }),
          }
        );
        const payResult = await payResponse.json();
        if (payResult.error) {
          this.$message.error(payResult.error);
          return;
        }
        this.paymentDialogVisible = true;
        this.qrCodeUrl = `https://image-charts.com/chart?chs=200x200&cht=qr&choe=UTF-8&chl=${payResult.qr_url}`;
        this.startPaymentTimer();
        this.startPaymentCheck();
      } catch (error) {
        this.$message.error("支付请求失败，请稍后重试");
        console.error("支付请求失败:", error);
      } finally {
        this.isPaying = false;
      }
    },

    // 开始检查支付状态
    startPaymentCheck() {
      this.paymentCheckInterval = setInterval(async () => {
        await this.checkPaymentStatus();
      }, 1000); // 每1秒检查一次
    },

    startPaymentTimer() {
      const createTime = new Date(
        this.currentOrder.createTime.replace(" ", "T")
      ); // 兼容 Safari
      const expireAt = new Date(createTime.getTime() + 15 * 60 * 1000); // 创建时间 + 15分钟

      this.paymentTimer = setInterval(() => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expireAt - now) / 1000)); // 剩余秒数

        if (remaining > 0) {
          const minutes = Math.floor(remaining / 60);
          const seconds = remaining % 60;
          this.expireTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;
        } else {
          this.expireTime = "0:00";
          clearInterval(this.paymentTimer);
          this.cancelPayment();
        }
      }, 1000);
    },

    // 修改支付状态检查方法
    async checkPaymentStatus() {
      try {
        const response = await fetch(
          `https://www.xiaoliangtongxue.cn/api/pay/status/${this.order_id}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        const result = await response.json();

        if (result.status === "paid") {
          this.clearPaymentTimers();
          this.$message.success("支付成功！请稍后刷新订单列表，查看订单状态。", {
            duration: 0,
          });
          this.paymentDialogVisible = false;
          await this.fetchOrders();

          // 如果是批量支付，更新所有选中订单的状态
          if (this.selectedOrders.length > 1) {
            this.selectedOrders = [];
          }
        }
      } catch (error) {
        console.error("检查支付状态失败:", error);
      }
    },

    // 修改取消支付方法
    cancelPayment() {
      this.clearPaymentTimers();
      this.paymentDialogVisible = false;
      this.selectedOrders = [];
      this.totalPrice = 0;
      this.originalPrice = 0;
      this.discountAmount = 0;
      this.selectedCoupon = null;
      this.$message.info("已取消支付");
    },

    clearPaymentTimers() {
      if (this.paymentTimer) {
        clearInterval(this.paymentTimer);
        this.paymentTimer = null;
      }
      if (this.paymentCheckInterval) {
        clearInterval(this.paymentCheckInterval);
        this.paymentCheckInterval = null;
      }
    },

    // 组件销毁时清理定时器
    beforeDestroy() {
      this.clearPaymentTimers();
    },

    // 新增：处理表格多选
    handleSelectionChange(selection) {
      this.selectedOrders = selection;
      this.recalculatePrice();
    },

    // 新增：批量支付
    async batchPayment() {
      if (this.selectedCoupon) {
        if (this.originalPrice < this.selectedCoupon.min_amount) {
          this.$message.warning(
            `订单金额未满${this.selectedCoupon.min_amount}元，不能使用该优惠券`
          );
          return;
        }
      }

      this.$confirm(
        `确定要支付选中的 ${this.selectedOrders.length} 个订单吗？${
          this.selectedCoupon
            ? `\n将使用优惠券，优惠后实付：¥${this.totalPrice.toFixed(2)}`
            : ""
        }`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          this.showPayments(this.selectedOrders);
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "已取消支付",
          });
        });
    },

    async showPayments(orders) {
      if (this.isPaying) {
        this.$message.warning("正在处理支付请求，请勿重复点击");
        return;
      }

      this.isPaying = true;

      try {
        const orderId = `PRINT_${String(orders[0].orderId).padStart(6, "1")}`;
        this.order_id = orderId;
        const order_amount = Math.max(0, this.totalPrice).toFixed(2);

        const paymentData = {
          order_no: orderId,
          amount: order_amount,
          coupon_id: this.selectedCoupon ? this.selectedCoupon.id : null,
          orders: orders.map((order) => order.orderId),
        };

        console.log("创建支付请求参数:", paymentData);

        const payResponse = await fetch(`${API_BASE_URL}/pay`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(paymentData),
        });

        if (!payResponse.ok) {
          const errorData = await payResponse.json();
          throw new Error(errorData.error || "支付请求失败");
        }

        const payResult = await payResponse.json();

        if (payResult.zero_amount) {
          this.$message.success("使用优惠券支付成功！");
          await this.fetchOrders();
          return;
        }

        this.paymentDialogVisible = true;
        this.qrCodeUrl = `https://image-charts.com/chart?chs=200x200&cht=qr&choe=UTF-8&chl=${payResult.qr_url}`;
        this.startPaymentCheck();
      } catch (error) {
        console.error("创建支付请求失败:", error);
        this.$message.error(error.message || "创建支付请求失败，请重试");
      } finally {
        this.isPaying = false;
      }
    },

    // 新增：获取优惠券列表
    async fetchCoupons() {
      const userId = localStorage.getItem("userId");
      try {
        if (!userId) {
          console.log("未登录，跳过获取优惠券");
          return;
        }

        const response = await fetch(
          `https://www.xiaoliangtongxue.cn/api/coupon/available?user_id=${userId}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        const data = await response.json();
        console.log("获取到的优惠券:", data); // 添加日志

        if (Array.isArray(data)) {
          this.availableCoupons = data;
        } else {
          console.error("Invalid coupon data format:", data);
          this.$message.error("获取优惠券失败");
        }
      } catch (error) {
        console.error("获取优惠券失败:", error);
        this.$message.error("获取优惠券失败");
      }
    },

    // 新增：获取优惠券标签
    getCouponLabel(coupon) {
      let label = "";
      if (coupon.type === "fixed") {
        label = `满减券 ${coupon.code}`;
        if (coupon.min_amount > 0) {
          label += ` (满${coupon.min_amount}可用)`;
        }
      } else {
        label = `折扣券 ${coupon.code}`;
        label += ` (${(coupon.value / 10).toFixed(1)}折)`;
        if (coupon.min_amount > 0) {
          label += ` (满${coupon.min_amount}可用)`;
        }
      }
      return label;
    },

    // 新增：计算优惠后的总价
    recalculatePrice() {
      this.originalPrice = this.selectedOrders.reduce(
        (sum, order) => sum + order.pay,
        0
      );

      if (this.selectedCoupon) {
        if (this.selectedCoupon.type === "fixed") {
          this.discountAmount = this.selectedCoupon.value;
        } else {
          this.discountAmount =
            (this.selectedCoupon.value / 1000) * this.originalPrice;
        }
      } else {
        this.discountAmount = 0;
      }

      // 修改：如果优惠金额大于原价，则将优惠金额设置为原价
      if (this.discountAmount > this.originalPrice) {
        this.discountAmount = this.originalPrice;
      }

      this.totalPrice = Math.max(0, this.originalPrice - this.discountAmount);
    },
  },
};
</script>

<style scoped>
.my-orders-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.header {
  text-align: center;
  position: relative;
  padding-bottom: 16px;
}

.header h1 {
  color: #000000;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 1px;
}

/* 表格样式 */
:deep(.el-table) {
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  flex: 1;
  --el-table-border-color: #000000;
  --el-table-header-bg-color: #ffffff;
  margin-bottom: 16px;
}

:deep(.el-table--border::after),
:deep(.el-table--group::after),
:deep(.el-table::before) {
  background-color: #000000;
}

:deep(.el-table__inner-wrapper::before) {
  background-color: #000000;
}

:deep(.el-table__body-wrapper) {
  overflow-x: auto !important;
  height: calc(100% - 47px) !important;
  margin-bottom: 0;
}

:deep(.el-table__fixed-right) {
  height: 100% !important;
  background-color: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
}

:deep(.el-table__fixed-right-patch) {
  background-color: #f5f7fa;
}

:deep(.el-table__fixed-right .el-table__fixed-header-wrapper) {
  background-color: #f5f7fa;
}

:deep(.el-table__fixed-right .el-table__fixed-body-wrapper) {
  background-color: #fff;
}

.operation-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.operation-buttons :deep(.el-button) {
  padding: 6px 16px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.3s;
  background-color: #ffffff;
  border: 1px solid #000000;
  color: #000000;
}

.operation-buttons :deep(.el-button:hover) {
  transform: translateY(-1px);
  background-color: #000000;
  color: #ffffff;
}

.operation-buttons :deep(.el-button--primary) {
  background-color: #000000;
  border-color: #000000;
  color: #ffffff;
}

.operation-buttons :deep(.el-button--primary:hover) {
  background-color: #333333;
  border-color: #333333;
}

.operation-buttons :deep(.el-button--danger) {
  background-color: #000000;
  border-color: #000000;
  color: #ffffff;
}

.operation-buttons :deep(.el-button--danger:hover) {
  background-color: #333333;
  border-color: #333333;
}

/* 表格列宽调整 */
:deep(.el-table th) {
  background-color: var(--el-table-header-bg-color);
  color: #000000;
  font-weight: 600;
  height: 50px;
  padding: 8px 0;
  text-align: center;
}

:deep(.el-table td) {
  padding: 12px 0;
  color: #333333;
  text-align: center;
}

:deep(.el-table--enable-row-hover .el-table__body tr:hover > td) {
  background-color: #f5f5f5 !important;
}

/* 状态标签样式 */
:deep(.el-tag) {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid #000000;
  background-color: #ffffff;
  color: #000000;
}

:deep(.el-tag--warning) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--primary) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--info) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--success) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--danger) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

/* 批量操作按钮样式 */
.batch-actions {
  margin-bottom: 16px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #000000;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.batch-payment-info {
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: flex-end;
}

.batch-payment-info :deep(.el-button--primary) {
  background-color: #000000;
  border-color: #000000;
  color: #ffffff;
}

.batch-payment-info :deep(.el-button--primary:hover) {
  background-color: #333333;
  border-color: #333333;
}

.coupon-selection {
  flex: 0 0 auto;
}

.price-info {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 14px;
}

.original-price {
  color: #909399;
  text-decoration: line-through;
}

.discount-amount {
  color: #67c23a;
}

.final-price {
  color: #000000;
  font-size: 16px;
  font-weight: bold;
}

.coupon-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.coupon-type {
  background-color: #409eff;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.coupon-value {
  color: #f56c6c;
  font-weight: bold;
}

.coupon-condition {
  color: #909399;
  font-size: 12px;
}

/* 订单详情样式 */
.order-detail {
  padding: 20px;
}

:deep(.el-descriptions) {
  --el-descriptions-border-color: #000000;
}

:deep(.el-descriptions__label) {
  background-color: #f5f5f5;
  color: #000000;
}

:deep(.el-descriptions__content) {
  color: #333333;
}

.el-main {
  padding: 20px;
  display: flex;
  flex-direction: column;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .my-orders-container {
    padding: 10px;
  }

  :deep(.el-table) {
    font-size: 14px;
  }

  .operation-buttons {
    flex-direction: column;
    align-items: center;
  }
}

.actual-price {
  color: #f56c6c;
  font-weight: bold;
}

/* 订单详情弹窗样式 */
:deep(.el-dialog) {
  border: 2px solid #000000;
  border-radius: 8px;
}

:deep(.el-dialog__body) {
  background-color: #ffffff;
}

/* 添加支付弹窗相关样式 */
.payment-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

/* 支付弹窗中显示多个订单信息 */
.order-info {
  text-align: center;
  margin-bottom: 20px;
  width: 100%;
}

.order-list {
  margin: 10px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.order-list-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}

.order-list-item:last-child {
  border-bottom: none;
}

.order-list-item .order-id {
  color: #606266;
}

.order-list-item .order-price {
  color: #f56c6c;
  font-weight: bold;
}

.qr-code-container {
  width: 200px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  background: #f8f9fa;
  border-radius: 8px;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-code-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #909399;
}

.payment-tips {
  text-align: center;
  color: #606266;
}

.payment-tips p {
  margin: 5px 0;
}

.expire-time {
  color: #f56c6c;
  font-weight: bold;
}

/* 分页器样式 */
.pagination-container {
  margin-top: 0;
  padding: 12px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #000000;
}

.pagination-container :deep(.el-pagination) {
  justify-content: center;
  font-weight: normal;
}

.pagination-container :deep(.el-pagination__total),
.pagination-container :deep(.el-pagination__sizes) {
  margin-right: 16px;
  line-height: 28px;
  color: #333333;
}

.pagination-container :deep(.el-pager li) {
  border-radius: 4px;
  line-height: 28px;
  color: #333333;
}

.pagination-container :deep(.el-pager li.is-active) {
  background-color: #000000;
  color: #ffffff;
  font-weight: bold;
}

.pagination-container :deep(.el-pager li:hover) {
  color: #000000;
}

.file-list {
  margin-top: 20px;
  padding: 16px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
}

.file-list h3 {
  margin: 0 0 16px 0;
  color: #000000;
}

.file-operations {
  display: flex;
  gap: 12px;
}

:deep(.el-button) {
  padding: 6px 16px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.3s;
  background-color: #ffffff;
  border: 1px solid #000000;
  color: #000000;
}

:deep(.el-button:hover) {
  transform: translateY(-1px);
  background-color: #000000;
  color: #ffffff;
}
</style>